import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { latest, type Artifact } from '../api/releases.ts';
import { formatBytes } from '../lib/format.ts';
import { EmptyState } from '../components/common/EmptyState.tsx';
import { IconCloud, IconDownload } from '../lib/icons.tsx';

/**
 * 앱 다운로드 화면.
 * 설명을 늘어놓지 않는다 — 받아서 실행하면 되는 프로그램이라 버튼 하나면 충분하다.
 * Windows 실행파일(raw)을 기본으로 하고, 없을 때만 다른 형식으로 내려간다.
 */
export function DownloadPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['client-latest'],
    queryFn: () => latest('windows', 'amd64'),
    staleTime: 5 * 60_000,
  });

  const release = data?.release ?? null;

  const primary: Artifact | null = useMemo(() => {
    const windows = (release?.artifacts ?? []).filter((a) => a.os === 'windows');
    const order = ['raw', 'zip', 'msi'];
    return [...windows].sort(
      (a, b) => order.indexOf(a.kind) - order.indexOf(b.kind),
    )[0] ?? null;
  }, [release]);

  return (
    <div className="dl">
      <div className="dl__inner" style={{ maxWidth: 520 }}>
        <div className="dl__brand"><IconCloud size={26} /> YumSync</div>

        {isLoading && <div className="empty"><span className="spinner" /></div>}

        {error && (
          <EmptyState title="다운로드 정보를 불러오지 못했습니다"
            description={(error as Error).message} />
        )}

        {!isLoading && !error && !primary && (
          <EmptyState
            icon={<IconDownload size={40} className="faint" />}
            title="아직 배포된 버전이 없습니다"
          />
        )}

        {primary && (
          <div className="dl__hero" style={{ justifyContent: 'center', textAlign: 'center' }}>
            <div className="dl__hero-main">
              <div className="dl__os">Windows용 다운로드</div>
              <div className="dl__meta">
                버전 {release?.version} · {formatBytes(primary.size)}
              </div>
              <a
                className="dl__cta"
                href={primary.url}
                download
                style={{ marginTop: 16 }}
              >
                <IconDownload size={18} /> 받기
              </a>
              <div className="dl__meta" style={{ marginTop: 14, lineHeight: 1.7 }}>
                설치 과정 없습니다. 받은 파일을 실행하면
                <br />
                로그인 창이 뜨고, 폴더를 고르면 바로 동기화됩니다.
              </div>
              <details className="dl__note">
                <summary>백신이 파일을 막는다면</summary>
                <p>
                  코드 서명 인증서가 없어 일부 백신이 &lsquo;알 수 없는 파일&rsquo;로 분류합니다.
                  오탐입니다.
                </p>
                <p>
                  <b>지금 바로 쓰려면</b> — 파일 하나가 아니라 파일을 둔 <b>폴더</b>를
                  검사 제외 목록에 넣는 편이 확실합니다.
                </p>
                <ul>
                  <li>알약 — 보호 기능 → 실시간 검사 → 검사 제외 설정</li>
                  <li>V3 — 환경 설정 → 검사 예외 설정</li>
                  <li>Windows 보안 — 바이러스 및 위협 방지 → 설정 관리 → 제외 항목 추가</li>
                </ul>
                <p>
                  <b>근본 해결</b> — 오탐 신고를 넣으면 다음 패턴 업데이트부터 모든 PC에서
                  자동으로 풀립니다.{' '}
                  <a href="https://www.alyac.com/scan/false_positive/" target="_blank" rel="noreferrer">
                    알약 신고
                  </a>
                  {' · '}
                  <a
                    href="https://www.ahnlab.com/site/securityinfo/reportFalsePositive.do"
                    target="_blank"
                    rel="noreferrer"
                  >
                    V3 신고
                  </a>
                </p>
                <p>
                  아래 SHA-256 으로 받은 파일이 맞는지 확인할 수 있습니다.
                  PowerShell 에서 <code>Get-FileHash yumsync.exe</code>.
                </p>
                {primary.sha256 && <code className="dl__hash">{primary.sha256}</code>}
              </details>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
